import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocaleChange } from '../../hooks/useLocaleChange';
import MenuBook from '@mui/icons-material/MenuBook';
import Shield from '@mui/icons-material/Shield';
import Straighten from '@mui/icons-material/Straighten';
import Psychology from '@mui/icons-material/Psychology';
import Restaurant from '@mui/icons-material/Restaurant';
import Group from '@mui/icons-material/Group';
import Category from '@mui/icons-material/Category';
import Download from '@mui/icons-material/Download';
import Home from '@mui/icons-material/Home';
import { useWikiTypes } from '../../contexts/WikiTypesContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { getErrorMessage } from '../../api/utils/error-handler';
import { wikiNewApi } from '../../api/wiki-new.service';
import { resolveApiSource } from '../../utils/wikiSourceResolver';
import UniversalSidebar, { SidebarSection, SidebarItem } from './UniversalSidebar';
import ServerSelection from '../ServerSelection';
import {
  FlattenedProfession,
  ProfessionHierarchyInfo,
  flattenProfessionsWithDepth,
  buildProfessionHierarchyMap,
} from '../../utils/professionHierarchy';

// Icon mapping for categories
const getCategoryIcon = (categoryName: string) => {
  const name = categoryName.toLowerCase();
  if (name.includes('оружие') || name.includes('weapon')) return <Straighten />;
  if (name.includes('броня') || name.includes('armor')) return <Shield />;
  if (name.includes('навык') || name.includes('skill')) return <Psychology />;
  if (name.includes('предмет') || name.includes('item')) return <Category />;
  if (name.includes('рецепт') || name.includes('recipe')) return <Restaurant />;
  if (name.includes('нпс') || name.includes('npc')) return <Group />;
  return <MenuBook />;
};

/**
 * Creates prefix elements for sidebar display of profession hierarchy.
 * Uses shared hierarchy map for color assignment.
 */
const createProfessionPrefixes = (
  flatProfessions: FlattenedProfession[],
  hierarchyMap: Map<number, ProfessionHierarchyInfo>,
): Map<number, React.ReactNode> => {
  const prefixMap = new Map<number, React.ReactNode>();

  flatProfessions.forEach(({ profession, depth }) => {
    const hierarchy = hierarchyMap.get(profession.id);
    if (!hierarchy) return;

    const { color, hasChildren, isRoot } = hierarchy;

    let prefix: React.ReactNode;
    if (isRoot && hasChildren) {
      prefix = <span style={{ color, marginRight: '6px', fontSize: '14px' }}>◆</span>;
    } else {
      prefix = (
        <span style={{ marginRight: '6px' }}>
          <span style={{ opacity: 0.3 }}>{'  '.repeat(depth - 1)}└</span>
          <span style={{ color, fontSize: '12px' }}>◇</span>
        </span>
      );
    }

    prefixMap.set(profession.id, prefix);
  });

  return prefixMap;
};

interface WikiAppSidebarProps {
  mobileOpen: boolean;
  onDrawerToggle: () => void;
  /** Сайдбар свёрнут на десктопе, чтобы контент занимал всю ширину. */
  collapsed?: boolean;
}

const WikiAppSidebar: React.FC<WikiAppSidebarProps> = ({ mobileOpen, onDrawerToggle, collapsed = false }) => {
  const { t } = useTranslation();
  const { categories, setCategories } = useWikiTypes();
  const [loading, setLoading] = useState(false);
  const isMobile = useMediaQuery('(max-width: 869px)');
  const { showError } = useSnackbar();

  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await wikiNewApi.getWikiTypes();
      setCategories(response.categories);
    } catch (err: unknown) {
      showError(getErrorMessage(err, t('wiki:messages.loadCategoriesError')));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setCategories]);

  // Load categories on mount
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // Reload categories when language changes
  useLocaleChange(loadCategories);

  // Convert wiki categories to sidebar items
  const wikiItems = useMemo((): SidebarItem[] => {
    return categories.map(category => {
      // Resolve API source from category
      const categoryApiSource = resolveApiSource([category.source]);
      const categoryPath = `/wiki/${categoryApiSource}`;

      // Combine subcategories and races
      const allSubItems: SidebarItem[] = [];

      // Add subcategories
      if (category.subCategories) {
        const subCategoryItems = category.subCategories.map(subCategory => {
          // Resolve API source from hierarchy: category -> subCategory
          const subCategoryApiSource = resolveApiSource([category.source, subCategory.source]);

          // ALL subcategories use subcategoryId in URL for unique identification
          const subPath = `/wiki/${subCategoryApiSource}/subcategories/${subCategory.id}`;

          // Handle subcategory types (for hierarchical structure)
          const subCategorySubItems: SidebarItem[] = [];

          if (subCategory.types) {
            subCategory.types.forEach(type => {
              // Resolve API source from hierarchy: category -> subCategory -> type
              const typeApiSource = resolveApiSource([category.source, subCategory.source, type.source]);

              if (type.subTypes && type.subTypes.length > 0) {
                // Type with subtypes
                const typeSubItems = type.subTypes.map(subType => {
                  // Resolve API source from full hierarchy
                  const subTypeApiSource = resolveApiSource([category.source, subCategory.source, type.source, subType.source]);
                  return {
                    id: `subtype-${subType.id}`,
                    text: subType.name,
                    // Include subcategoryId in path
                    path: `/wiki/${subTypeApiSource}/subcategories/${subCategory.id}/types/${type.id}/subtypes/${subType.id}`,
                  };
                });

                subCategorySubItems.push({
                  id: `type-${type.id}`,
                  text: type.name,
                  subItems: typeSubItems,
                });
              } else {
                // Type without subtypes
                subCategorySubItems.push({
                  id: `type-${type.id}`,
                  text: type.name,
                  // Include subcategoryId in path
                  path: `/wiki/${typeApiSource}/subcategories/${subCategory.id}/types/${type.id}`,
                });
              }
            });
          }

          return {
            id: `subcategory-${subCategory.id}`,
            text: subCategory.name,
            path: subCategorySubItems.length === 0 ? subPath : undefined,
            subItems: subCategorySubItems.length > 0 ? subCategorySubItems : undefined,
          };
        });

        allSubItems.push(...subCategoryItems);
      }

      // Add races (for Skills category)
      if (category.races) {
        const raceItems = category.races.map(race => {
          // Resolve API source from hierarchy: category -> race
          const raceApiSource = resolveApiSource([category.source, race.source]);
          const racePath = `/wiki/${raceApiSource}/races/${race.id}`;

          // Handle skillTypes
          const raceSubItems: SidebarItem[] = [];

          if (race.skillTypes) {
            race.skillTypes.forEach(skillType => {
              // Resolve API source from hierarchy: category -> race -> skillType
              const skillTypeApiSource = resolveApiSource([category.source, race.source, skillType.source]);

              if (skillType.classes && skillType.classes.length > 0) {
                // SkillType with classes
                const skillTypeSubItems: SidebarItem[] = [];

                skillType.classes.forEach(classItem => {
                  // Resolve API source from hierarchy: category -> race -> skillType -> class
                  const classApiSource = resolveApiSource([category.source, race.source, skillType.source, classItem.source]);

                  if (classItem.professions && classItem.professions.length > 0) {
                    // Class with professions - flatten with colored hierarchy
                    const flatProfessions = flattenProfessionsWithDepth(classItem.professions);
                    const hierarchyMap = buildProfessionHierarchyMap(classItem.professions);
                    const prefixMap = createProfessionPrefixes(flatProfessions, hierarchyMap);

                    const classSubItems = flatProfessions.map(({ profession }) => {
                      const professionApiSource = resolveApiSource([category.source, race.source, skillType.source, classItem.source, profession.source]);
                      return {
                        id: `profession-${profession.id}`,
                        text: profession.name,
                        prefixElement: prefixMap.get(profession.id),
                        path: `/wiki/${professionApiSource}/races/${race.id}/types/${skillType.id}/classes/${classItem.id}/professions/${profession.id}`,
                      };
                    });

                    skillTypeSubItems.push({
                      id: `class-${classItem.id}`,
                      text: classItem.name,
                      subItems: classSubItems,
                    });
                  } else {
                    // Class without professions
                    skillTypeSubItems.push({
                      id: `class-${classItem.id}`,
                      text: classItem.name,
                      path: `/wiki/${classApiSource}/races/${race.id}/types/${skillType.id}/classes/${classItem.id}`,
                    });
                  }
                });

                raceSubItems.push({
                  id: `skilltype-${skillType.id}`,
                  text: skillType.name,
                  subItems: skillTypeSubItems,
                });
              } else {
                // SkillType without classes
                raceSubItems.push({
                  id: `skilltype-${skillType.id}`,
                  text: skillType.name,
                  path: `/wiki/${skillTypeApiSource}/races/${race.id}/types/${skillType.id}`,
                });
              }
            });
          }

          return {
            id: `race-${race.id}`,
            text: race.name,
            path: raceSubItems.length === 0 ? racePath : undefined,
            subItems: raceSubItems.length > 0 ? raceSubItems : undefined,
          };
        });

        allSubItems.push(...raceItems);
      }

      return {
        id: `category-${category.id}`,
        text: category.name,
        icon: getCategoryIcon(category.name),
        path: allSubItems.length === 0 ? categoryPath : undefined, // no direct path if has subcategories/races
        subItems: allSubItems.length > 0 ? allSubItems : undefined,
      };
    });
  }, [categories]);

  // Quick links
  const quickLinks = useMemo((): SidebarItem[] => [
    { id: 'home', text: t('navigation:menu.home'), icon: <Home />, path: '/' },
    { id: 'download', text: t('navigation:footer.download'), icon: <Download />, path: '/downloads' },
  ], [t]);

  // Sidebar sections
  const sections = useMemo((): SidebarSection[] => {
    // On mobile, add quick links at the end of menu items
    const allItems = isMobile ? [...wikiItems, ...quickLinks] : wikiItems;

    return [
      {
        id: 'server-section',
        customContent: <ServerSelection />,
        fixed: true,
      },
      {
        id: 'wiki-menu-section',
        withMenuSection: true,
        items: allItems,
      },
    ];
  }, [wikiItems, quickLinks, isMobile]);

  return (
    <UniversalSidebar
      sections={sections}
      mobileOpen={mobileOpen}
      onDrawerToggle={onDrawerToggle}
      loading={loading}
      collapsed={collapsed}
    />
  );
};

export default WikiAppSidebar;